import { BaseModel } from './BaseModel';
import LearningUnit from './LearningUnit';
import PostResponse from './PostResponse';
import Learner from './Learner';
import Assessor from './Assessor';
import { isBoolean } from 'lodash';
import IssuerUser from './IssuerUser';

export class EvidenceSubmission extends BaseModel {
  static get entity() {
    return 'evidence_submissions';
  }

  static get fk() {
    return 'evidence_submission_id';
  }

  static get STATUS() {
    return {
      NOT_SUBMITTED: 'Pending Submission',
      REVIEW_PENDING: 'Reviewing',
      IN_PROGRESS_BY_ASSESSOR: 'In Progress by Assessor',
      IN_PROGRESS_BY_ISSUER: 'In Progress by Issuer',
      DENIED: 'Denied',
      ACHIEVED: 'Achieved',
      AWARDED: 'Awarded'
    };
  }

  static get EnumStatus() {
    return {
      NotSubmitted: 'NOT_SUBMITTED',
      ReviewPending: 'REVIEW_PENDING',
      Denied: 'DENIED',
      Awarded: 'AWARDED'
    };
  }

  static fields() {
    return {
      id: this.attr(null),
      code: this.attr(null),
      pass: this.attr(null),
      status: this.string('NOT_SUBMITTED'),
      is_latest: this.boolean(false),
      learning_unit_id: this.attr(null),
      learning_unit: this.belongsTo(LearningUnit, 'learning_unit_id'),
      user_id: this.attr(null),
      user: this.belongsTo(Learner, 'user_id'),
      post_response_ids: this.attr([]),
      post_responses: this.hasManyBy(PostResponse, 'post_response_ids', 'post_id'),
      submitted_at: this.attr(null),
      created_at: this.attr(null),
      updated_at: this.attr(null),
      delete_at: this.attr(null),
      assessor_reviewed_at: this.attr(null),
      assessor_id: this.attr(null),
      assessor: this.belongsTo(Assessor, 'assessor_id'),
      issuer_reviewed_at: this.attr(null),
      issuer_id: this.attr(null),
      issuer: this.belongsTo(IssuerUser, 'issuer_id'),
      feedback_for_earner: this.attr(null),
      comment_for_issuer: this.attr(null),
      assessor_recommendation: this.attr(null),
      submitted_no: this.attr(null),
      previous_evidence_submission: this.attr(null),
      is_feedback_editing: this.boolean(false),
      design_suggestion: this.attr(null)
    };
  }

  isAssessor(userId) {
    return this.assessor_id === userId;
  }

  get readableStatus() {
    if (this.submitted_at && !this.assessor_reviewed_at) {
      return EvidenceSubmission.STATUS.IN_PROGRESS_BY_ASSESSOR;
    }

    if (this.submitted_at && this.assessor_reviewed_at && !this.issuer_reviewed_at) {
      return EvidenceSubmission.STATUS.IN_PROGRESS_BY_ISSUER;
    }

    return EvidenceSubmission.STATUS[this.status];
  }

  static apiConfig = {
    actions: {
      fetch(params) {
        return this.get(`/evidence-submissions`, { params });
      },

      fetchLearner(learningUnitId) {
        return this.get(`/evidence-submissions/learner`, {
          params: { learning_unit: learningUnitId }
        });
      },

      initial(learningUnitId) {
        return this.post('/evidence-submissions/initial', {
          learning_unit: learningUnitId
        });
      },

      fetchAssessor(learningUnitId, params = {}) {
        return this.get('/evidence-submissions/assessor', {
          params: { learning_unit: learningUnitId, ...params }
        });
      },

      fetchIssuer(learningUnitId, params = {}) {
        return this.get('/evidence-submissions/issuer', {
          params: { learning_unit: learningUnitId, ...params }
        });
      },

      fetchGrantObem(learningUnitId, params = {}) {
        return this.get('/evidence-submissions/grant-obem', {
          params: { learning_unit: learningUnitId, ...params }
        });
      },

      async findWithPagination(params = {}, { insertLearningUnit = true } = {}) {
        const {
          response: { data = {} }
        } = await this.get('/evidence-submissions/pagination', {
          params,
          save: false
        });

        let { evidenceSubmissions = [] } = data;

        let insertedEvidenceSubmissions = [];

        if (evidenceSubmissions.length) {
          if (!insertLearningUnit) {
            evidenceSubmissions = evidenceSubmissions.map((evidenceSubmission) => {
              const { learning_unit, ...otherProps } = evidenceSubmission;

              let learning_unit_id = null;

              if (learning_unit?.id) {
                learning_unit_id = learning_unit.id;
              } else if (Number.isInteger(learning_unit)) {
                learning_unit_id = learning_unit;
              }

              return {
                ...otherProps,
                learning_unit_id
              };
            });
          }

          for (const evidenceSubmission of evidenceSubmissions) {
            const insertResults = await EvidenceSubmission.insert({
              data: evidenceSubmission
            });

            insertedEvidenceSubmissions = [
              ...insertedEvidenceSubmissions,
              ...insertResults.evidence_submissions
            ];
          }

          // insertedEvidenceSubmissions = insertResult.evidence_submissions || [];
        }

        return {
          evidenceSubmissions: insertedEvidenceSubmissions,
          totalItems: data.totalItems
        };
      },

      submit(evidenceSubmissionId) {
        return this.post(`/evidence-submissions/${evidenceSubmissionId}/submit`);
      },

      resubmit(learningUnitId) {
        return this.post('/evidence-submissions/resubmit', {
          learning_unit: learningUnitId
        });
      },

      assessByAssessor(evidenceSubmissionId, body = {}) {
        return this.post(`/evidence-submissions/${evidenceSubmissionId}/assess/assessor`, body);
      },

      grant(evidenceSubmissionId, body = {}) {
        return this.post(`/evidence-submissions/${evidenceSubmissionId}/grant`, body);
      },

      update(id, body) {
        return this.put(`/evidence-submissions/${id}`, body);
      },
      updateByAssessor(id, body) {
        return this.put(`/evidence-submissions/${id}/update-by-assessor`, body);
      },

      async assignAssessor(body = {}) {
        const {
          response: { data = {} }
        } = await this.patch(`/evidence-submissions/assign`, body, { save: false });

        if (data && data.data && data.data.length) {
          const submissions = data.data;
          Assessor.insertOrUpdate({ data: submissions.map((submission) => submission.assessor) });
          return EvidenceSubmission.insertOrUpdate({ data: submissions });
        } else {
          return null;
        }
      },
      async canFeedback(evidenceSubmissionId) {
        const {
          response: { data = {} }
        } = await this.get(`/evidence-submissions/${evidenceSubmissionId}/can-feedback`, {
          save: false
        });

        return data;
      }

      //   destroy(id) {
      //     return this.delete(`/criteria/${id}`, {
      //       delete: id,
      //     });
      //   },
    }
  };

  get isNotSubmitted() {
    return this.status === 'NOT_SUBMITTED';
  }

  get isReviewPending() {
    return this.status === 'REVIEW_PENDING';
  }

  get isReviewPendingByAssessor() {
    return this.status === 'REVIEW_PENDING' && !this.assessor_reviewed_at;
  }

  get isReviewPendingByIssuer() {
    return (
      this.status === 'REVIEW_PENDING' && this.assessor_reviewed_at && !this.issuer_reviewed_at
    );
  }

  get isDenied() {
    return this.status === 'DENIED';
  }

  get isAwarded() {
    return this.status === 'AWARDED';
  }

  // The project period ended before the learner could submit: denied with no
  // submitted date (and therefore no assessor review).
  get isProjectEnded() {
    return this.isDenied && !this.submitted_at;
  }

  static getLatestEvidenceSubmission(learningUnitId, userId) {
    return EvidenceSubmission.query()
      .where('learning_unit_id', learningUnitId)
      .where('user_id', userId)
      .orderBy('created_at', 'desc')
      .first();
  }

  // Latest submission excluding project-ended ones (denied with no assessor
  // review), i.e. the latest submission the learner can actually act on.
  static getLatestSubmittableEvidenceSubmission(learningUnitId, userId) {
    return EvidenceSubmission.query()
      .where('learning_unit_id', learningUnitId)
      .where('user_id', userId)
      .orderBy('created_at', 'desc')
      .get()
      .find((submission) => !submission.isProjectEnded);
  }

  get hasInvalidUploadFiles() {
    const postResponses = PostResponse.query()
      .where('assessment_submission', (value) => value !== null)
      .whereIdIn(this.post_response_ids)
      .get();

    const hasErrorUploadFiles =
      postResponses && postResponses.length
        ? postResponses.some(({ error_upload_ids = [] }) => error_upload_ids.length > 0)
        : false;

    return hasErrorUploadFiles;
  }

  get canSubmit() {
    const postResponses = PostResponse.query()
      .where('assessment_submission', (value) => value !== null)
      .whereIdIn(this.post_response_ids)
      .get();

    const hasAllEvidences =
      postResponses && postResponses.length
        ? postResponses.every(({ description = '', attachments = [] }) => {
            return (
              description ||
              (attachments && attachments.length)
            );
          })
        : false;

    const hasAllDoneUpload =
      postResponses && postResponses.length
        ? postResponses.every(({ uploading_upload_ids = [] }) => uploading_upload_ids.length === 0)
        : false;

    return this.isNotSubmitted && hasAllEvidences && hasAllDoneUpload;
  }

  get canAssess() {
    return this.isEvaluationCompleted();
  }

  // The issuer cannot edit "Comments for Issuer Only", so a submission without
  // that comment must still be grantable.
  get canGrant() {
    return this.isEvaluationCompleted({ requireCommentForIssuer: false });
  }

  isEvaluationCompleted({ requireCommentForIssuer = true } = {}) {
    const postResponses = PostResponse.query()
      .whereIdIn(this.post_response_ids)
      .where('evidence_submission_id', this.id)
      .where('assessment_evaluation_id', (value) => value !== null)
      .with('assessment_evaluation')
      .get();

    const hasAllCriteria =
      postResponses && postResponses.length
        ? postResponses.every((post_response) => {
            return isBoolean(post_response.assessment_evaluation.pass);
          })
        : false;

    const hasCommentForIssuer =
      !requireCommentForIssuer ||
      Boolean(this.comment_for_issuer && this.comment_for_issuer.length > 0);

    const hasAllFeedback = !this.is_feedback_editing && hasCommentForIssuer;

    const hasAssessmentResult = isBoolean(this.pass);

    const hasAllCriteriaEditedDone =
      postResponses && postResponses.length
        ? postResponses.every(({ is_editing = false }) => is_editing === false)
        : false;

    return (
      this.isReviewPending &&
      hasAllCriteria &&
      hasAllFeedback &&
      hasAssessmentResult &&
      hasAllCriteriaEditedDone
    );
  }

  get isFirstSubmission() {
    return this.submitted_no === 1 || !this.previous_evidence_submission;
  }
}

export default EvidenceSubmission;
